import { useState, useMemo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination,
    TableSortLabel,
  } from '@mui/material';
  import { useDateSort } from '../../../hooks/useDateSort';
  import TableActionsMenu from '../../../components/common/TableActionsMenu';
  import { AdcashAvatyeRevenueByApp } from '../../../types/adcash.types';
  
  type Props = {
    data: AdcashAvatyeRevenueByApp[];
    appList: string[];
    exchangeRate: number;
    onExchangeRateChange: (rate: number) => void;
  };
  
  export default function AdcashAvatyeRevenueSummaryTable({ data, appList, exchangeRate, onExchangeRateChange }: Props) {
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    const formatCurrency = useCallback((value: number) => {
        return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    }, []);

    // 날짜별로 그룹화하고 앱별 매출 계산
    const aggregatedDataRaw = useMemo(() => {
      const allDates = new Set<string>();
      data.forEach(stat => {
        allDates.add(stat.date);
      });
      const sortedDates = Array.from(allDates);

      return sortedDates.map(date => {
        const dateStats = data.filter(stat => stat.date === date);
        const totalRevenue = dateStats.reduce((sum, stat) => sum + stat.revenue, 0) * exchangeRate;
        
        const appRevenues: Record<string, number> = {};
        appList.forEach(app => {
          const appStats = dateStats.filter(stat => stat.appName === app);
          appRevenues[app] = appStats.reduce((sum, stat) => sum + stat.revenue, 0) * exchangeRate;
        });

        return {
          date,
          totalRevenue,
          appRevenues
        };
      });
    }, [data, appList, exchangeRate]);

    const { sorted: aggregatedData, sortDirection, toggleSort } = useDateSort(aggregatedDataRaw);

    const paged = useMemo(
        () => aggregatedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [aggregatedData, page]
    );

    // 합계 계산
    const totals = useMemo(() => {
        const totalRevenue = aggregatedData.reduce((sum, row) => sum + row.totalRevenue, 0);
        const appTotals: Record<string, number> = {};
        appList.forEach(app => {
            appTotals[app] = aggregatedData.reduce((sum, row) => sum + (row.appRevenues[app] || 0), 0);
        });
        
        return {
            totalRevenue,
            appTotals
        };
    }, [aggregatedData, appList]);

    const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');

    // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시, 합계 행 제외)
    const allRowsDataForExcel = useMemo(() => {
        return aggregatedData.map((row) => {
            return [
                row.date,
                formatNumberOnly(row.totalRevenue),
                ...appList.map((app) => formatNumberOnly(row.appRevenues[app] || 0))
            ];
        });
    }, [aggregatedData, appList]);

    return (
        <Box>
    {/* 환율 버튼 1230 or 1350 */}
        <Box
          sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2
                }}
            >
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {[1230, 1350].map(rate => (
                        <Button
                            key={rate}
                            size="small"
                            variant="outlined"
                            onClick={() => onExchangeRateChange(rate)}
                            sx={{
                                borderColor: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                                color: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                                fontWeight: 'bold',
                                '&:hover': {
                                    borderColor: exchangeRate === rate ? '#0044D2' : '#757575',
                                    backgroundColor: exchangeRate === rate ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                                }
                            }}
                        >
                            환율 {rate}
                        </Button>
                    ))}
                </Box>
                <TableActionsMenu tableRef={tableRef} allRowsData={allRowsDataForExcel} />
            </Box>
            <TableContainer
                sx={{
                    overflowX: 'auto'
                }}
            >
              <Table ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={toggleSort}
                      >
                        날짜
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>총 매출</TableCell>
                    {appList.map((app) => (
                      <TableCell key={app} align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{app}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                    {/* 합계 행 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>합계</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {formatCurrency(totals.totalRevenue)}
                        </TableCell>
                        {appList.map((app) => (
                          <TableCell key={app} align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {formatCurrency(totals.appTotals[app] || 0)}
                          </TableCell>
                        ))}
                    </TableRow>
                    {paged.map((row) => {
                        return (
                            <TableRow key={row.date}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.totalRevenue)}</TableCell>
                                {appList.map((app) => (
                                  <TableCell key={app} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatCurrency(row.appRevenues[app] || 0)}
                                  </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={aggregatedData.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
            />
        </Box>
    );
  }
  